import React, { useEffect, useState, useRef } from 'react';
import type { IntlShape } from 'react-intl-next';
import { useIntl } from 'react-intl-next';
// We have deprecated unstated. Please use react-sweet-state instead
// eslint-disable-next-line no-restricted-imports
import { Subscribe } from 'unstated';

import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { useSharedPluginStateSelector } from '@atlaskit/editor-common/use-shared-plugin-state-selector';
import { JSONTransformer } from '@atlaskit/editor-json-transformer';
import type { DocNode } from '@atlaskit/adf-schema';

import type { WithFlagsProps, FlagDescriptor } from '@confluence/flags';
import { withFlags } from '@confluence/flags';
import { PageTreeStateUpdater } from '@confluence/page-tree-refresh-state-container';

import type {
	PluginConfiguration,
	ConflictResolutionUIPlugin,
} from '../conflictResolutionUIPluginType';

import { createPage } from './createPageQuery';
import { messages } from './messages';

export const ContentComponent = withFlags(
	({
		flags,
		config: { spaceId, contentId },
		api,
	}: {
		config: PluginConfiguration;
		api: ExtractInjectionAPI<ConflictResolutionUIPlugin> | undefined;
	} & WithFlagsProps) => {
		const intl = useIntl();
		const lastReconnectionConflictMetadata = useSharedPluginStateSelector(
			api,
			'collabEdit.lastReconnectionConflictMetadata',
		);
		// We can't create another page while trying to create a page
		const isCreatingPage = useRef(false);
		useEffect(() => {
			if (lastReconnectionConflictMetadata?.offlineDoc && spaceId && contentId) {
				// Show conflict occurred
				flags.showFlag({
					id: 'editor_reconnection_conflict',
					type: 'warning',
					title: intl.formatMessage(messages.createCopyFlagTitle),
					description: intl.formatMessage(messages.createCopyFlagDescription),
					actions: [
						{
							content: intl.formatMessage(messages.createCopyFlagCreateAction),
							onClick: async () => {
								if (isCreatingPage.current) {
									return;
								}
								isCreatingPage.current = true;
								try {
									const response = await createPage(
										spaceId,
										contentId,
										new JSONTransformer().encode(
											lastReconnectionConflictMetadata?.offlineDoc,
										) as DocNode,
									);
									if (!response.ok) {
										flags.showFlag(createErrorFlag(intl));
										isCreatingPage.current = false;
										return;
									}
									setShouldUpdatePageTree(true);
									flags.hideFlag('editor_reconnection_conflict');
									const data = await response.json();
									flags.showFlag(
										createCopySuccessFlag(`${data._links.base}${data._links.edituiv2}`, intl),
									);
								} catch (e) {
									flags.showFlag(createErrorFlag(intl));
								}
								isCreatingPage.current = false;
							},
						},
						{
							content: intl.formatMessage(messages.createCopyFlagNoAction),
							onClick: () => {
								flags.hideFlag('editor_reconnection_conflict');
							},
						},
					],
				});
			}
		}, [lastReconnectionConflictMetadata, spaceId, contentId, flags, intl]);

		const [shouldUpdatePageTree, setShouldUpdatePageTree] = useState(false);

		return (
			<Subscribe to={[PageTreeStateUpdater]}>
				{(pageStateUpdater: PageTreeStateUpdater) => {
					// Make sure we only push the update once
					if (shouldUpdatePageTree && contentId) {
						// Requests that we fetch the page tree with the newly restored page.
						pageStateUpdater.setForceFetchPageId(contentId);
						setShouldUpdatePageTree(false);
					}
					return null;
				}}
			</Subscribe>
		);
	},
);

const createErrorFlag = (intl: IntlShape): FlagDescriptor => ({
	id: 'editor-reconnection-conflict-copy-failed',
	type: 'error',
	title: intl.formatMessage(messages.errorFlagTitle),
	description: intl.formatMessage(messages.errorFlagDescription),
	close: 'auto',
});

const createCopySuccessFlag = (href: string, intl: IntlShape): FlagDescriptor => ({
	id: 'editor-reconnection-conflict-copy-created',
	type: 'success',
	title: intl.formatMessage(messages.copyCreatedFlagTitle),
	description: intl.formatMessage(messages.copyCreatedFlagDescription),
	close: 'auto',
	actions: [
		{
			content: intl.formatMessage(messages.copyCreatedFlagViewPage),
			href,
		},
	],
});
